import {
  RegistryId,
  Registry,
} from '@/react/portainer/registries/types/registry';
import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';

interface Props {
  value: RegistryId | null;
  onChange: (value: RegistryId | null) => void;
  /** Filter function to control which registries are shown */
  filter?: (registries: Array<Registry>) => Array<Registry>;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the selection can be cleared */
  isClearable?: boolean;
  /** Custom label for the form control */
  label?: string;
  /** Placeholder text for the select */
  placeholder?: string;
  /** Data-cy attribute for testing */
  'data-cy'?: string;
}

/**
 * A reusable registry selector component that fetches registries
 * and displays them in a select dropdown.
 */
export function RegistrySelector({
  value,
  onChange,
  filter = (registries) => registries,
  required = false,
  isClearable = false,
  label = 'Select registry',
  placeholder = 'Select a registry',
  'data-cy': dataCy = 'registry-select',
}: Props) {
  const registryOptionsQuery = useRegistries<Option<RegistryId>[]>({
    select: (registries) =>
      filter(registries).map((r) => ({ label: r.Name, value: r.Id })),
  });

  return (
    <FormControl label={label} required={required}>
      <PortainerSelect<RegistryId>
        value={value ?? 0}
        onChange={(selectedValue) => onChange(selectedValue || null)}
        options={registryOptionsQuery.data || []}
        placeholder={placeholder}
        bindToBody
        data-cy={dataCy}
        isLoading={registryOptionsQuery.isLoading}
        isClearable={isClearable}
      />
    </FormControl>
  );
}
